import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { User } from '@/types';
import { useWallet } from '../contexts/WalletContext';
import { contractAddresses } from '../data/contractAddresses';
import EventTicket from '../abis/EventTicket.json';
import { X, Sparkles, UploadCloud, Loader2, Network, ShieldAlert, RefreshCw } from 'lucide-react';
import { uploadToIPFS } from '../utils/ipfs';

interface Venue {
  id: string;
  venueName: string;
}

const CreateEvent = () => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    startTime: '',
    endTime: '',
    location: '',
    image: '',
    venueName: '',
    organizedBy: '',
    totalTickets: '0',
    ticketPrice: '0',
    ticketCurrency: 'USD',
    areTicketsNft: false,
    areTicketsFractionalized: false,
    nftName: '',
    nftSymbol: '',
    nftImage: null as File | null,
  });

  const [venues, setVenues] = useState<Venue[]>([]);
  const [organizers, setOrganizers] = useState<User[]>([]);
  const [isNftModalOpen, setIsNftModalOpen] = useState(false);

  useEffect(() => {
    const fetchVenues = async () => {
      try {
        const response = await fetch('/api/venues');
        if (response.ok) {
          const data = await response.json();
          setVenues(data);
        }
      } catch (error) {
        console.error('Failed to fetch venues:', error);
      }
    };
    
    const fetchOrganizers = async () => {
      try {
        const response = await fetch('/api/users?role=ORGANIZER');
        if (response.ok) {
          const data = await response.json();
          setOrganizers(data);
        }
      } catch (error) {
        console.error('Failed to fetch organizers:', error);
      }
    };

    fetchVenues();
    fetchOrganizers();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'file') {
      const file = (e.target as HTMLInputElement).files?.[0] || null;
      setFormData(prevState => ({ ...prevState, [name]: file }));
      return;
    }

    const checked = (e.target as HTMLInputElement).checked;

    if (name === 'areTicketsNft') {
      if (checked) {
        setIsNftModalOpen(true);
      } else {
        setFormData(prevState => ({
          ...prevState,
          areTicketsNft: false,
          areTicketsFractionalized: false,
        }));
      }
    } else {
      setFormData(prevState => ({
        ...prevState,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleVenueSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedVenueName = e.target.value;
    setFormData(prevState => ({ ...prevState, venueName: selectedVenueName }));
  };

  const handleOrganizerSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedOrganizer = e.target.value;
    setFormData(prevState => ({ ...prevState, organizedBy: selectedOrganizer }));
  };

  const handleNftSettingsSave = (settings: { name: string, symbol: string, image: File | null, areTicketsFractionalized: boolean }) => {
    setFormData(prevState => ({
      ...prevState,
      areTicketsNft: true,
      nftName: settings.name,
      nftSymbol: settings.symbol,
      nftImage: settings.image,
      areTicketsFractionalized: settings.areTicketsFractionalized,
    }));
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const finalDescription = `${formData.description}::END_TIME::${new Date(formData.endTime).toISOString()}`;

    const submissionData = {
        ...formData,
        description: finalDescription,
        date: formData.startTime, 
        totalTickets: parseInt(formData.totalTickets, 10),
        ticketPrice: parseFloat(formData.ticketPrice),
    };

    try {
      const response = await fetch('/api/events', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submissionData),
      });

      if (response.ok) {
        console.log('Event created successfully!');
      } else {
        const errorData = await response.json();
        console.error('Failed to create event:', errorData.error);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  return (
    <>
      <div className="bg-kala-900 text-white p-6 rounded-lg">
        <h1 className="text-2xl font-bold mb-6">Create a New Event</h1>
        <form onSubmit={handleSubmit}>
          
          <div className="mb-4">
            <label className="block text-kala-400 text-sm font-bold mb-2" htmlFor="name">Event Name</label>
            <input className="w-full bg-kala-800 border border-kala-700 rounded-lg px-4 py-2 text-white outline-none focus:border-kala-secondary" id="name" type="text" name="name" value={formData.name} onChange={handleChange} required />
          </div>

          <div className="mb-4">
            <label className="block text-kala-400 text-sm font-bold mb-2" htmlFor="description">Description</label>
            <textarea className="w-full bg-kala-800 border border-kala-700 rounded-lg px-4 py-2 text-white outline-none focus:border-kala-secondary" id="description" name="description" value={formData.description} onChange={handleChange} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-kala-400 text-sm font-bold mb-2" htmlFor="startTime">Start Time</label>
              <input className="w-full bg-kala-800 border border-kala-700 rounded-lg px-4 py-2 text-white outline-none focus:border-kala-secondary" id="startTime" type="datetime-local" name="startTime" value={formData.startTime} onChange={handleChange} required />
            </div>
            <div>
              <label className="block text-kala-400 text-sm font-bold mb-2" htmlFor="endTime">End Time</label>
              <input className="w-full bg-kala-800 border border-kala-700 rounded-lg px-4 py-2 text-white outline-none focus:border-kala-secondary" id="endTime" type="datetime-local" name="endTime" value={formData.endTime} onChange={handleChange} required />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-kala-400 text-sm font-bold mb-2" htmlFor="location">Location</label>
            <input className="w-full bg-kala-800 border border-kala-700 rounded-lg px-4 py-2 text-white outline-none focus:border-kala-secondary" id="location" type="text" name="location" value={formData.location} onChange={handleChange} required />
          </div>

          <div className="mb-4">
            <label className="block text-kala-400 text-sm font-bold mb-2" htmlFor="organizedBy">Organized By</label>
            <input className="w-full bg-kala-800 border border-kala-700 rounded-lg px-4 py-2 text-white outline-none focus:border-kala-secondary" id="organizedBy" type="text" name="organizedBy" value={formData.organizedBy} onChange={handleChange} required />
          </div>
          
          <div className="mb-4">
              <label className="block text-kala-400 text-sm font-bold mb-2" htmlFor="organizerId">Or Select an Existing Organizer (Optional)</label>
              <select className="w-full bg-kala-800 border border-kala-700 rounded-lg px-4 py-2 text-white outline-none focus:border-kala-secondary" id="organizerId" name="organizerId" onChange={handleOrganizerSelect}>
                  <option value="">Select an Organizer</option>
                  {organizers.map(org => (
                      <option key={org.id} value={org.name}>{org.name}</option>
                  ))}
              </select>
          </div>

          <div className="mb-4">
            <label className="block text-kala-400 text-sm font-bold mb-2" htmlFor="venueName">Venue Name</label>
            <input className="w-full bg-kala-800 border border-kala-700 rounded-lg px-4 py-2 text-white outline-none focus:border-kala-secondary" id="venueName" type="text" name="venueName" value={formData.venueName} onChange={handleChange} required />
          </div>
          
          <div className="mb-4">
              <label className="block text-kala-400 text-sm font-bold mb-2" htmlFor="venueId">Or Select an Existing Venue (Optional)</label>
              <select className="w-full bg-kala-800 border border-kala-700 rounded-lg px-4 py-2 text-white outline-none focus:border-kala-secondary" id="venueId" name="venueId" onChange={handleVenueSelect}>
                  <option value="">Select a Venue</option>
                  {venues.map(venue => (
                      <option key={venue.id} value={venue.venueName}>{venue.venueName}</option>
                  ))}
              </select>
          </div>

          <div className="mb-4">
            <label className="block text-kala-400 text-sm font-bold mb-2" htmlFor="image">Image URL</label>
            <input className="w-full bg-kala-800 border border-kala-700 rounded-lg px-4 py-2 text-white outline-none focus:border-kala-secondary" id="image" type="text" name="image" value={formData.image} onChange={handleChange} />
          </div>

          <h2 className="text-xl font-bold mb-4 mt-6">Ticketing</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-kala-400 text-sm font-bold mb-2" htmlFor="totalTickets">Total Tickets</label>
              <input className="w-full bg-kala-800 border border-kala-700 rounded-lg px-4 py-2 text-white outline-none focus:border-kala-secondary" id="totalTickets" type="number" name="totalTickets" value={formData.totalTickets} onChange={handleChange} />
            </div>
            <div>
              <label className="block text-kala-400 text-sm font-bold mb-2" htmlFor="ticketPrice">Price</label>
              <input className="w-full bg-kala-800 border border-kala-700 rounded-lg px-4 py-2 text-white outline-none focus:border-kala-secondary" id="ticketPrice" type="number" name="ticketPrice" value={formData.ticketPrice} onChange={handleChange} />
            </div>
            <div>
              <label className="block text-kala-400 text-sm font-bold mb-2" htmlFor="ticketCurrency">Currency</label>
              <input className="w-full bg-kala-800 border border-kala-700 rounded-lg px-4 py-2 text-white outline-none focus:border-kala-secondary" id="ticketCurrency" type="text" name="ticketCurrency" value={formData.ticketCurrency} onChange={handleChange} />
            </div>
          </div>

          <div className="mb-4">
              <label className="flex items-center">
                  <input type="checkbox" className="form-checkbox bg-kala-800 border-kala-700 text-kala-secondary focus:ring-kala-secondary" name="areTicketsNft" checked={formData.areTicketsNft} onChange={handleChange} />
                  <span className="ml-2 text-kala-300">Issue Tickets as NFTs</span>
              </label>
          </div>

          <div className="flex items-center justify-end">
            <button className="bg-kala-secondary text-kala-900 font-bold px-4 py-2 rounded-lg hover:bg-cyan-400 transition-colors" type="submit">
              Create Event
            </button>
          </div>

        </form>
      </div>

      {isNftModalOpen && (
        <NftSettingsModal
          isOpen={isNftModalOpen}
          onClose={() => setIsNftModalOpen(false)}
          onSave={handleNftSettingsSave}
          initialFractionalized={formData.areTicketsFractionalized}
          initialNftName={formData.nftName}
          initialNftSymbol={formData.nftSymbol}
          totalTickets={formData.totalTickets}
        />
      )}
    </>
  );
};

interface NftSettingsModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (settings: { name: string; symbol: string; image: File | null; areTicketsFractionalized: boolean }) => void;
    initialFractionalized: boolean;
    initialNftName: string;
    initialNftSymbol: string;
    totalTickets: string;
}

const NftSettingsModal: React.FC<NftSettingsModalProps> = ({ isOpen, onClose, onSave, initialFractionalized, initialNftName, initialNftSymbol, totalTickets }) => {
    const { provider, getChainId, switchNetwork } = useWallet();
    const [nftName, setNftName] = useState(initialNftName);
    const [nftSymbol, setNftSymbol] = useState(initialNftSymbol);
    const [nftImage, setNftImage] = useState<File | null>(null);
    const [areTicketsFractionalized, setAreTicketsFractionalized] = useState(initialFractionalized);
    const [isMinting, setIsMinting] = useState(false);
    const [mintingStep, setMintingStep] = useState('');
    const [networkName, setNetworkName] = useState<string | undefined>();

    useEffect(() => {
        const chainId = getChainId();
        const name = chainId === 80002 ? 'Amoy' : chainId === 137 ? 'Polygon' : 'Other';
        setNetworkName(name);
      }, [getChainId]);

    const handleMint = async () => {
        if (!provider || !nftImage) {
          alert('Please connect your wallet and select an image.');
          return;
        }

        const chainId = getChainId();
        if (chainId !== 80002) {
            alert('Please switch to the Amoy testnet to mint.');
            return;
        }

        setIsMinting(true);
        onSave({ 
            name: nftName, 
            symbol: nftSymbol, 
            image: nftImage, 
            areTicketsFractionalized 
        });

        let tokenId: string | undefined;

        try {
            const signer = await provider.getSigner();
            const userAddress = await signer.getAddress();

            // 1. Upload image to IPFS
            setMintingStep('Uploading image to IPFS...');
            const imageUrl = await uploadToIPFS(nftImage);

            // 2. Create metadata and upload to IPFS
            setMintingStep('Uploading metadata to IPFS...');
            const metadata = { name: nftName, description: "Event Ticket NFT", image: imageUrl };
            const metadataBlob = new Blob([JSON.stringify(metadata)], { type: 'application/json' });
            const metadataUrl = await uploadToIPFS(metadataBlob);

            // 3. Mint NFT
            setMintingStep('Sending transaction to mint NFT...');
            const ticketContract = new ethers.Contract(contractAddresses.EventTicket, EventTicket.abi, signer);
            const tx = await ticketContract.mint(userAddress, 0, parseInt(totalTickets, 10), []);
            const receipt = await tx.wait();

            // Ethers v6 event parsing: Manually parse logs
            const ticketInterface = new ethers.Interface(EventTicket.abi);
            if (receipt && receipt.logs) {
                for (const log of receipt.logs) {
                    try {
                        const parsedLog = ticketInterface.parseLog(log);
                        if (parsedLog && parsedLog.name === 'TransferSingle') {
                            // Found the event, get the tokenId
                            tokenId = parsedLog.args.id.toString();
                            break; 
                        }
                    } catch {
                        // This log is not from the EventTicket contract, so we can ignore it
                    }
                }
            }

            if (!tokenId) {
                throw new Error("Minting transaction succeeded, but the Token ID could not be parsed from the receipt.");
            }

            // 4. Save to DB
            setMintingStep('Saving NFT to database...');
            const dbResponse = await fetch('/api/nfts', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    ownerId: userAddress, // The backend will handle resolving this to a user ID
                    tokenId, 
                    contractAddress: contractAddresses.EventTicket, 
                    metadataUrl 
                }),
            });

            if (!dbResponse.ok) {
                const errorData = await dbResponse.json().catch(() => ({}));
                throw new Error(errorData.error || 'The backend failed to save the NFT after minting.');
            }

            setMintingStep(`Minting successful! Token ID: ${tokenId}`)
        } catch (error: unknown) {
            console.error("Minting failed:", error);
            let message = 'An unknown error occurred.';
            if (error instanceof Error) {
                const ethersError = error as { reason?: string; data?: { message?: string } };
                if (ethersError.reason) {
                    message = ethersError.reason;
                } else if (ethersError.data?.message) {
                    message = ethersError.data.message;
                } else {
                    message = error.message;
                }
            }
            setMintingStep(`Error: ${message}`)
        } 
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in" onClick={onClose}>
            <div className="bg-kala-900 border border-kala-700 rounded-2xl w-full max-w-md shadow-2xl overflow-hidden" onClick={e => e.stopPropagation()}>
                <div className="p-4 bg-kala-800 border-b border-kala-700 flex justify-between items-center">
                    <h3 className="text-white font-bold flex items-center gap-2"><Sparkles className="text-kala-secondary"/>NFT Ticket Settings</h3>
                    <button onClick={onClose} className="text-kala-400 hover:text-white"><X className="w-5 h-5" /></button>
                </div>
                {isMinting ? (
                    <div className="p-6 text-center">
                        <Loader2 className="w-10 h-10 text-kala-secondary animate-spin mx-auto mb-4" />
                        <p className="text-white font-semibold mb-2">Minting in Progress...</p>
                        <p className="text-kala-300 text-sm">{mintingStep}</p>
                        {mintingStep.includes('successful!') && (
                            <button onClick={onClose} className="mt-4 bg-kala-secondary text-kala-900 font-bold px-4 py-2 rounded-lg hover:bg-cyan-400 transition-colors">Done</button>
                        )}
                    </div>
                ) : (
                    <div className="p-6">
                        <div className="mb-4">
                            <label className="block text-kala-400 text-sm font-bold mb-2" htmlFor="nftName">Ticket NFT Name</label>
                            <input className="w-full bg-kala-800 border border-kala-700 rounded-lg px-4 py-2 text-white outline-none focus:border-kala-secondary" id="nftName" type="text" value={nftName} onChange={(e) => setNftName(e.target.value)} placeholder="e.g. KalaKrut Rock Fest Ticket" />
                        </div>
                        <div className="mb-4">
                            <label className="block text-kala-400 text-sm font-bold mb-2" htmlFor="nftSymbol">Ticket NFT Symbol</label>
                            <input className="w-full bg-kala-800 border border-kala-700 rounded-lg px-4 py-2 text-white outline-none focus:border-kala-secondary" id="nftSymbol" type="text" value={nftSymbol} onChange={(e) => setNftSymbol(e.target.value)} placeholder="e.g. KRF" />
                        </div>
                        <div className="mb-4">
                            <label className="block text-kala-400 text-sm font-bold mb-2" htmlFor="nftImage">Ticket Image</label>
                            <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-kala-700 border-dashed rounded-md">
                                <div className="space-y-1 text-center">
                                    {nftImage ? (
                                        <p className="text-green-400">{nftImage.name}</p>
                                    ) : (
                                        <>
                                            <UploadCloud className="mx-auto h-12 w-12 text-kala-500" />
                                            <div className="flex text-sm text-kala-400">
                                                <label htmlFor="nftImage" className="relative cursor-pointer bg-kala-800 rounded-md font-medium text-kala-secondary hover:text-cyan-400 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-kala-secondary">
                                                    <span>Upload a file</span>
                                                    <input id="nftImage" name="nftImage" type="file" className="sr-only" onChange={(e) => setNftImage(e.target.files ? e.target.files[0] : null)} />
                                                </label>
                                                <p className="pl-1">or drag and drop</p>
                                            </div>
                                            <p className="text-xs text-kala-500">PNG, JPG, GIF up to 10MB</p>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="mb-6">
                            <label className="flex items-center">
                                <input
                                    type="checkbox"
                                    className="form-checkbox bg-kala-800 border-kala-700 text-kala-secondary focus:ring-kala-secondary"
                                    checked={areTicketsFractionalized}
                                    onChange={(e) => setAreTicketsFractionalized(e.target.checked)}
                                />
                                <span className="ml-3 text-kala-300">Allow Fractional Ownership of Tickets</span>
                            </label>
                            <p className="text-xs text-kala-500 mt-2 ml-6">This allows ticket holders to sell fractions of their NFT tickets.</p>
                        </div>
                          <div className="bg-kala-900 p-4 rounded-lg border border-kala-800 mb-6">
                            <h3 className="font-semibold text-white mb-2">Transaction Details</h3>
                            <div className="flex items-center gap-3 mb-2">
                                <Network className="w-5 h-5 text-kala-secondary" />
                                <div>
                                    <p className="text-sm font-bold text-kala-200">Network</p>
                                    <p className="text-sm text-kala-300">{networkName || 'Not Connected'}</p>
                                </div>
                            </div>
                            {networkName !== 'Amoy' && (
                               <div className="bg-yellow-900/30 border border-yellow-500/50 rounded-lg p-4 flex flex-col items-start gap-3 mt-3">
                               <div className="flex items-start gap-3">
                                 <ShieldAlert className="w-5 h-5 text-yellow-400 shrink-0 mt-0.5" />
                                 <div className="flex-grow">
                                   <p className="text-sm font-semibold text-yellow-200">Incorrect Network</p>
                                   <p className="text-xs text-yellow-300 mt-1">
                                     This feature requires the Amoy testnet. Please switch your network to proceed.
                                   </p>
                                 </div>
                               </div>
                               <button 
                                 onClick={() => switchNetwork('80002')}
                                 className="ml-auto mt-2 flex items-center gap-2 text-xs bg-yellow-500/20 text-yellow-100 font-bold py-2 px-3 rounded-md hover:bg-yellow-500/40 transition-colors"
                               >
                                 <RefreshCw className="w-3 h-3" />
                                 Switch to Amoy
                               </button>
                             </div>
                            )}
                        </div>
                        <div className="flex justify-end gap-4">
                            <button onClick={onClose} className="text-kala-300 font-bold px-4 py-2 rounded-lg hover:bg-kala-800 transition-colors">Cancel</button>
                            <button 
                                onClick={handleMint} 
                                disabled={networkName !== 'Amoy'}
                                className="bg-kala-secondary text-kala-900 font-bold px-4 py-2 rounded-lg hover:bg-cyan-400 transition-colors disabled:bg-kala-700 disabled:cursor-not-allowed"
                            >
                                Confirm & Mint
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default CreateEvent;